import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Leaf, Check, Shield, Heart, Sparkles, Users, CreditCard, Loader2, Gift } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [membershipInfo, setMembershipInfo] = useState(null);
  const [fetchingStatus, setFetchingStatus] = useState(true);

  useEffect(() => {
    fetchMembershipInfo();
  }, []);

  const fetchMembershipInfo = async () => {
    try {
      const response = await axios.get(`${API_URL}/payments/membership-status`);
      setMembershipInfo(response.data);
      
      // If fully paid, redirect to dashboard or onboarding
      if (response.data.is_fully_paid) {
        navigate(user?.is_onboarded ? '/dashboard' : '/onboarding');
      }
    } catch (error) {
      console.error('Failed to fetch membership info:', error);
    } finally {
      setFetchingStatus(false);
    }
  };

  const handlePayment = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/payments/create-checkout`, {
        origin_url: window.location.origin
      });
      
      // Redirect to Stripe Checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
      setLoading(false);
    }
  };

  const features = [
    { icon: <Users className="w-5 h-5" />, text: "Unlimited profile browsing" },
    { icon: <Heart className="w-5 h-5" />, text: "Send unlimited likes" },
    { icon: <Sparkles className="w-5 h-5" />, text: "Access all compatibility quizzes" },
    { icon: <Shield className="w-5 h-5" />, text: "Privacy-protected messaging" },
  ];

  if (fetchingStatus) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const monthsPaid = membershipInfo?.months_paid || 0;
  const monthsRequired = membershipInfo?.months_required || 4;
  const nextPaymentAmount = membershipInfo?.next_payment_amount || 5;
  const currentMonth = monthsPaid + 1;
  const progressPercent = (monthsPaid / monthsRequired) * 100;

  return (
    <div className="min-h-screen bg-background" data-testid="payment-page">
      {/* Header */}
      <div className="border-b border-border">
        <div className="container-app py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
            <div className="w-10 h-10 rounded-full gradient-primary flex items-center justify-center">
              <Leaf className="w-5 h-5 text-white" strokeWidth={1.5} />
            </div>
            <span className="text-2xl font-serif font-semibold">Twig</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container-app py-12">
        <div className="max-w-xl mx-auto text-center">
          <Badge className="mb-4 rounded-full bg-primary/10 text-primary">
            Month {currentMonth} of {monthsRequired}
          </Badge>
          
          <h1 className="text-4xl font-serif font-semibold mb-4">
            {monthsPaid === 0 ? 'Become a Twig Member' : 'Continue Your Membership'}
          </h1>
          
          <p className="text-lg text-muted-foreground mb-8">
            {monthsPaid === 0 
              ? 'Just 4 monthly payments, then lifetime free access!'
              : `${monthsRequired - monthsPaid} more payment${monthsRequired - monthsPaid > 1 ? 's' : ''} until lifetime free access!`
            }
          </p>

          {/* Progress Card */}
          {monthsPaid > 0 && (
            <Card className="rounded-2xl border-border/50 shadow-soft mb-6">
              <CardContent className="py-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Payment Progress</span>
                  <span className="text-sm text-muted-foreground">{monthsPaid}/{monthsRequired} months</span>
                </div>
                <Progress value={progressPercent} className="h-3 mb-2" />
                <p className="text-xs text-muted-foreground">
                  {monthsRequired - monthsPaid === 1 
                    ? 'Last payment! After this, you have lifetime access.' 
                    : `${monthsRequired - monthsPaid} payments remaining`
                  }
                </p>
              </CardContent>
            </Card>
          )}

          <Card className="rounded-2xl border-primary/20 shadow-float mb-8">
            <CardHeader className="text-center pb-2">
              <CardTitle className="text-5xl font-serif font-bold text-primary">
                ${nextPaymentAmount}
              </CardTitle>
              <p className="text-muted-foreground">
                Month {currentMonth} of {monthsRequired} • Then FREE forever
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3 text-left">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-green-50 flex items-center justify-center text-green-600">
                      <Check className="w-4 h-4" />
                    </div>
                    <span>{feature.text}</span>
                  </div>
                ))}
                
                {/* Lifetime access highlight */}
                <div className="flex items-center gap-3 pt-2 border-t border-border mt-2">
                  <div className="w-8 h-8 rounded-full bg-amber-50 flex items-center justify-center text-amber-600">
                    <Gift className="w-4 h-4" />
                  </div>
                  <span className="font-medium">Lifetime FREE access after 4 months</span>
                </div>
              </div>

              <div className="pt-4">
                <Button 
                  onClick={handlePayment}
                  disabled={loading}
                  className="w-full h-14 text-lg rounded-xl"
                  data-testid="pay-now-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5 mr-2" />
                      Pay ${nextPaymentAmount} for Month {currentMonth}
                    </>
                  )}
                </Button>
              </div>

              <p className="text-xs text-muted-foreground text-center">
                Secure payment powered by Stripe. No auto-renewal.
              </p>
            </CardContent>
          </Card>

          {/* Payment Schedule */}
          <Card className="rounded-2xl border-border/50 shadow-soft mb-6">
            <CardContent className="py-6">
              <h3 className="font-medium mb-4 text-left">Your Payment Schedule</h3>
              <div className="space-y-2">
                {[1, 2, 3, 4].map((month) => (
                  <div 
                    key={month} 
                    className={`flex items-center justify-between p-3 rounded-lg ${
                      month <= monthsPaid 
                        ? 'bg-green-50' 
                        : month === currentMonth 
                          ? 'bg-primary/10 border border-primary/30' 
                          : 'bg-muted/50'
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      {month <= monthsPaid ? (
                        <Check className="w-4 h-4 text-green-600" />
                      ) : month === currentMonth ? (
                        <CreditCard className="w-4 h-4 text-primary" />
                      ) : (
                        <div className="w-4 h-4 rounded-full border-2 border-muted-foreground/30" />
                      )}
                      Month {month}
                    </span>
                    <span className={month <= monthsPaid ? 'text-green-600 font-medium' : 'text-muted-foreground'}>
                      {month <= monthsPaid ? 'Paid ✓' : `$${nextPaymentAmount}`}
                    </span>
                  </div>
                ))}
                <div className="flex items-center justify-between p-3 rounded-lg bg-amber-50 border border-amber-200">
                  <span className="flex items-center gap-2">
                    <Gift className="w-4 h-4 text-amber-600" />
                    After Month 4
                  </span>
                  <span className="text-amber-600 font-medium">FREE Forever!</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="p-4 rounded-xl bg-muted/50 text-sm text-muted-foreground">
            <p>
              <strong>Why monthly payments?</strong> This helps us maintain a quality community 
              and ensures committed members. After 4 months, you're in for life—completely free!
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;
